/**
 * ============================================================================
 * VARIABLE REFERENCE SUGGESTION DROPDOWN
 * ============================================================================
 *
 * Autocomplete dropdown that appears when typing `{{` in a text editor.
 * Shows available variables/types and objects/glossary items, grouped and
 * searchable. Supports keyboard navigation (arrow keys, Enter, Escape).
 *
 * @module editor/VariableRefSuggestion
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { VariableRefSuggestionItem } from '@/lib/utils/variableReferences';

// ============================================================================
// TYPES
// ============================================================================

interface VariableRefSuggestionProps {
  /** Whether the dropdown is visible */
  isOpen: boolean;
  /** Position relative to the container element */
  position: { x: number; y: number };
  /** Filtered suggestion items to display */
  items: VariableRefSuggestionItem[];
  /** Called when user selects an item */
  onSelect: (refString: string) => void;
  /** Called when user wants to close the dropdown (Escape) */
  onClose: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_VISIBLE_ITEMS = 8;

// ============================================================================
// COMPONENT
// ============================================================================

export const VariableRefSuggestion: React.FC<VariableRefSuggestionProps> = ({
  isOpen,
  position,
  items,
  onSelect,
  onClose,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLDivElement>(null);

  // Reset active index when items change
  useEffect(() => {
    setActiveIndex(0);
  }, [items]);

  // Scroll active item into view
  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen || items.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex((prev) => (prev + 1) % items.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          e.stopPropagation();
          if (items[activeIndex]) {
            onSelect(items[activeIndex].refString);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
      }
    },
    [isOpen, items, activeIndex, onSelect, onClose]
  );

  // Attach keyboard listener
  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, handleKeyDown]);

  if (!isOpen || items.length === 0) return null;

  // Group items by source type
  const variableItems = items.filter((i) => i.sourceType === 'variable');
  const objectItems = items.filter((i) => i.sourceType === 'object');

  // Build a flat list with group headers for rendering
  const renderItems: Array<
    | { type: 'header'; label: string }
    | { type: 'item'; item: VariableRefSuggestionItem; flatIndex: number }
  > = [];

  let flatIndex = 0;

  if (variableItems.length > 0) {
    renderItems.push({ type: 'header', label: 'Variables' });
    for (const item of variableItems) {
      renderItems.push({ type: 'item', item, flatIndex });
      flatIndex++;
    }
  }

  if (objectItems.length > 0) {
    renderItems.push({ type: 'header', label: 'Objects' });
    for (const item of objectItems) {
      renderItems.push({ type: 'item', item, flatIndex });
      flatIndex++;
    }
  }

  return (
    <div
      ref={listRef}
      className="
        absolute z-50
        w-72 max-h-64 overflow-y-auto
        rounded-md border border-border bg-popover text-popover-foreground
        shadow-md
        animate-in fade-in zoom-in-95 duration-100
      "
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      role="listbox"
      aria-label="Variable reference suggestions"
    >
      {renderItems.map((entry, idx) => {
        if (entry.type === 'header') {
          return (
            <div
              key={`header-${entry.label}`}
              className="
                px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider
                text-muted-foreground bg-muted/40
                select-none sticky top-0
              "
            >
              {entry.label}
            </div>
          );
        }

        const { item, flatIndex: itemFlatIndex } = entry;
        const isActive = itemFlatIndex === activeIndex;

        return (
          <div
            key={item.id}
            ref={isActive ? activeItemRef : undefined}
            role="option"
            aria-selected={isActive}
            className={`
              px-3 py-1.5 cursor-pointer text-sm
              flex items-center gap-2
              transition-colors duration-75
              ${isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
            `}
            onMouseDown={(e) => {
              // Use mousedown (not click) to fire before blur
              e.preventDefault();
              onSelect(item.refString);
            }}
            onMouseEnter={() => setActiveIndex(itemFlatIndex)}
          >
            {/* Source type indicator */}
            <span
              className={`
                flex-shrink-0 w-1.5 h-1.5 rounded-full
                ${item.sourceType === 'variable' ? 'bg-blue-500' : 'bg-emerald-500'}
              `}
            />

            {/* Reference string */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1 truncate">
                <span className="text-muted-foreground text-xs">{item.parentLabel}.</span>
                <span className="font-medium">{item.fieldLabel}</span>
              </div>
              {item.currentValue != null && (
                <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                  = {String(item.currentValue)}
                </div>
              )}
            </div>

            {/* Type badge */}
            <span
              className={`
                flex-shrink-0 text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded
                ${
                  item.sourceType === 'variable'
                    ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                    : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                }
              `}
            >
              {item.sourceType === 'variable' ? 'VAR' : 'OBJ'}
            </span>
          </div>
        );
      })}

      {/* Empty state */}
      {items.length === 0 && (
        <div className="px-3 py-4 text-center text-sm text-muted-foreground">
          No matching references found
        </div>
      )}
    </div>
  );
};
